// DynamicSkin — programmatic replacement for GWEN's `DefaultSkin.png`.
//
// Rasterizes the entire UI atlas (panels, buttons, glyphs, scrollbars, etc.)
// into a 512×512 OffscreenCanvas at startup, uploads it to the WebGL2
// renderer as a single texture page, and exposes a `Map` from upstream region
// names (e.g. `Tab.Active.Bottom`) to UV/margin metadata that T009 (the
// 9-slice draw helper) consumes.
//
// Contract:
//   * No external assets — every pixel is generated by `drawRegion`.
//   * The atlas page mirrors `Gwen::Skin::TexturedBase::Init` byte-positions
//     so existing GWEN tooling that reads palette colors via `pixelColour`
//     keeps working (we still bake the strip at y=500 / y=508).
//   * `colors` mirrors the upstream `TexturedBase Colors_t` field shape but
//     with TS naming conventions (camelCase). Includes `button.bright/dark`
//     extensions to support the panel/groupbox bright/dark variants T009+
//     needs but GWEN's color table didn't surface.

import { color, type Color } from '../core/Structures';
import type { Texture } from '../renderer/Texture';
import { texture } from '../renderer/Texture';
import type { WebGL2Renderer } from '../renderer/WebGL2Renderer';
import {
  BAKED_ROW_500,
  BAKED_ROW_508,
  PALETTE,
  REGIONS,
  type RegionDescriptor,
} from './AtlasRegions';

// ---------- Public types ----------

export interface RegionInfo {
  type: 'bordered' | 'single';
  x: number;
  y: number;
  w: number;
  h: number;
  marginLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  uv: [number, number, number, number];
}

export interface SkinColors {
  window: { titleActive: Color; titleInactive: Color };
  button: {
    normal: Color;
    hover: Color;
    down: Color;
    disabled: Color;
    bright: Color;
    dark: Color;
  };
  tab: {
    active: { normal: Color };
    inactive: { normal: Color };
  };
  label: { default: Color; bright: Color; dark: Color; highlight: Color };
  tree: { lines: Color; normal: Color; hover: Color; selected: Color };
  properties: {
    line_normal: Color;
    line_selected: Color;
    line_hover: Color;
    title: Color;
    column_normal: Color;
    column_selected: Color;
    column_hover: Color;
    label_normal: Color;
    label_selected: Color;
    label_hover: Color;
    border: Color;
  };
  modalBackground: Color;
  tooltipText: Color;
  category: {
    header: Color;
    header_closed: Color;
    line: {
      text: Color;
      text_hover: Color;
      text_selected: Color;
      button: Color;
      button_hover: Color;
      button_selected: Color;
    };
    line_alt: {
      text: Color;
      text_hover: Color;
      text_selected: Color;
      button: Color;
      button_hover: Color;
      button_selected: Color;
    };
  };
}

// ---------- Internals ----------

const ATLAS_SIZE = 512;

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
type AnyCtx = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

function makeAtlasCanvas(): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(ATLAS_SIZE, ATLAS_SIZE);
  }
  const c = document.createElement('canvas');
  c.width = ATLAS_SIZE;
  c.height = ATLAS_SIZE;
  return c;
}

// Hex → Color. Accepts '#rgb', '#rrggbb', or 'rgba(r,g,b,a)' — the only
// formats used in PALETTE / BAKED_ROW_*.
function parseHex(s: string): Color {
  if (s.startsWith('rgba')) {
    // 'rgba(0,0,0,0.47)' → split inside parens.
    const inner = s.slice(s.indexOf('(') + 1, s.lastIndexOf(')'));
    const parts = inner.split(',').map((p) => parseFloat(p));
    return color(parts[0] | 0, parts[1] | 0, parts[2] | 0, Math.round((parts[3] ?? 1) * 255));
  }
  let h = s.startsWith('#') ? s.slice(1) : s;
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  const n = parseInt(h, 16);
  return color((n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff, 255);
}

interface PatchStyle {
  fill?: string | CanvasGradient;
  stroke?: string;
  borderRadius?: number;
  bevelLight?: string;
  bevelDark?: string;
}

// drawPatch — covers ~90% of bordered regions. Paints a (optionally
// rounded) filled rect with a 1px stroke and optional 1px inner bevel.
// Coordinates are aligned to the pixel grid; strokes use the +0.5 trick
// so 1px lines render crisp.
function drawPatch(
  ctx: AnyCtx,
  x: number,
  y: number,
  w: number,
  h: number,
  style: PatchStyle,
): void {
  const r = style.borderRadius ?? 0;
  const fill = style.fill;
  const stroke = style.stroke;

  if (fill) {
    ctx.fillStyle = fill;
    if (r > 0) {
      roundRectPath(ctx, x, y, w, h, r);
      ctx.fill();
    } else {
      ctx.fillRect(x, y, w, h);
    }
  }

  if (style.bevelLight) {
    // Top + left inner highlight, 1px.
    ctx.fillStyle = style.bevelLight;
    ctx.fillRect(x + 1, y + 1, w - 2, 1);
    ctx.fillRect(x + 1, y + 1, 1, h - 2);
  }
  if (style.bevelDark) {
    // Bottom + right inner shadow, 1px.
    ctx.fillStyle = style.bevelDark;
    ctx.fillRect(x + 1, y + h - 2, w - 2, 1);
    ctx.fillRect(x + w - 2, y + 1, 1, h - 2);
  }

  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1;
    if (r > 0) {
      roundRectPath(ctx, x + 0.5, y + 0.5, w - 1, h - 1, r);
      ctx.stroke();
    } else {
      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    }
  }
}

function roundRectPath(
  ctx: AnyCtx,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w * 0.5, h * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function vGradient(
  ctx: AnyCtx,
  x: number,
  y: number,
  h: number,
  top: string,
  bot: string,
): CanvasGradient {
  const g = ctx.createLinearGradient(x, y, x, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bot);
  return g;
}

function hGradient(
  ctx: AnyCtx,
  x: number,
  y: number,
  w: number,
  left: string,
  right: string,
): CanvasGradient {
  const g = ctx.createLinearGradient(x, y, x + w, y);
  g.addColorStop(0, left);
  g.addColorStop(1, right);
  return g;
}

// ---------- Glyph painters ----------

function drawCheckmark(ctx: AnyCtx, x: number, y: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + 3, y + 8);
  ctx.lineTo(x + 6, y + 11);
  ctx.lineTo(x + 12, y + 4);
  ctx.stroke();
  ctx.lineCap = 'butt';
}

function drawRadioDot(ctx: AnyCtx, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x + 7.5, y + 7.5, 3, 0, Math.PI * 2);
  ctx.fill();
}

function drawCircleFrame(
  ctx: AnyCtx,
  x: number,
  y: number,
  fill: string,
  border: string,
): void {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(x + 7.5, y + 7.5, 6.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x + 7.5, y + 7.5, 6.5, 0, Math.PI * 2);
  ctx.stroke();
}

// 31×31 cell with a centered pictogram (the X / box / underscore / restore
// glyphs that decorate the title-bar buttons).
type WindowGlyph = 'close' | 'maxi' | 'restore' | 'mini';
function drawWindowControl(
  ctx: AnyCtx,
  x: number,
  y: number,
  state: 'normal' | 'hover' | 'down',
  glyph: WindowGlyph,
): void {
  // Background plate matches the title-bar shading.
  let top: string;
  let bot: string;
  let glyphColor = '#ffffff';
  if (state === 'normal') {
    top = PALETTE.titleActiveTop;
    bot = PALETTE.titleActiveBottom;
  } else if (state === 'hover') {
    top = '#a4d4f0';
    bot = '#5cb0e0';
  } else {
    top = '#3878ac';
    bot = '#205c90';
  }
  drawPatch(ctx, x + 4, y + 4, 23, 23, {
    fill: vGradient(ctx, x + 4, y + 4, 23, top, bot),
    stroke: '#205c90',
    borderRadius: 2,
  });

  // Pictogram is 11×11 centred at (x+15, y+15).
  ctx.strokeStyle = glyphColor;
  ctx.fillStyle = glyphColor;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  if (glyph === 'close') {
    ctx.beginPath();
    ctx.moveTo(x + 11, y + 11);
    ctx.lineTo(x + 20, y + 20);
    ctx.moveTo(x + 20, y + 11);
    ctx.lineTo(x + 11, y + 20);
    ctx.stroke();
  } else if (glyph === 'maxi') {
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x + 11.5, y + 11.5, 9, 9);
    ctx.fillRect(x + 11, y + 11, 10, 2);
  } else if (glyph === 'restore') {
    // Two overlapping squares.
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x + 13.5, y + 9.5, 7, 7);
    ctx.fillStyle = top;
    ctx.fillRect(x + 11, y + 12, 7, 7);
    ctx.strokeStyle = glyphColor;
    ctx.strokeRect(x + 10.5, y + 11.5, 7, 7);
  } else {
    // mini = underscore.
    ctx.fillRect(x + 11, y + 19, 10, 2);
  }
  ctx.lineCap = 'butt';
}

// 15×15 cell with a centred triangular arrow.
type ArrowDir = 'left' | 'top' | 'right' | 'bottom';
function drawTriangle(
  ctx: AnyCtx,
  x: number,
  y: number,
  dir: ArrowDir,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  const cx = x + 7.5;
  const cy = y + 7.5;
  const r = 3.5;
  if (dir === 'left') {
    ctx.moveTo(cx - r, cy);
    ctx.lineTo(cx + r, cy - r);
    ctx.lineTo(cx + r, cy + r);
  } else if (dir === 'right') {
    ctx.moveTo(cx + r, cy);
    ctx.lineTo(cx - r, cy - r);
    ctx.lineTo(cx - r, cy + r);
  } else if (dir === 'top') {
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx - r, cy + r);
    ctx.lineTo(cx + r, cy + r);
  } else {
    ctx.moveTo(cx, cy + r);
    ctx.lineTo(cx - r, cy - r);
    ctx.lineTo(cx + r, cy - r);
  }
  ctx.closePath();
  ctx.fill();
}

// 7×7 numeric up/down spinner glyph.
function drawSpinnerArrow(
  ctx: AnyCtx,
  x: number,
  y: number,
  up: boolean,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  if (up) {
    ctx.moveTo(x + 3.5, y + 1.5);
    ctx.lineTo(x + 0.5, y + 5.5);
    ctx.lineTo(x + 6.5, y + 5.5);
  } else {
    ctx.moveTo(x + 3.5, y + 5.5);
    ctx.lineTo(x + 0.5, y + 1.5);
    ctx.lineTo(x + 6.5, y + 1.5);
  }
  ctx.closePath();
  ctx.fill();
}

// 15×15 slider thumb. Filled rounded rect with a centre notch.
function drawSliderThumb(
  ctx: AnyCtx,
  x: number,
  y: number,
  state: 'normal' | 'hover' | 'down' | 'disabled',
): void {
  let top: string;
  let bot: string;
  let border: string;
  if (state === 'normal') {
    top = PALETTE.buttonNormalTop;
    bot = PALETTE.buttonNormalBot;
    border = PALETTE.buttonBorder;
  } else if (state === 'hover') {
    top = PALETTE.buttonHoverTop;
    bot = PALETTE.buttonHoverBot;
    border = PALETTE.buttonBorder;
  } else if (state === 'down') {
    top = PALETTE.buttonPressedTop;
    bot = PALETTE.buttonPressedBot;
    border = PALETTE.buttonBorder;
  } else {
    top = PALETTE.buttonDisabled;
    bot = PALETTE.buttonDisabled;
    border = '#b0b0b0';
  }
  drawPatch(ctx, x + 1, y + 1, 13, 13, {
    fill: vGradient(ctx, x + 1, y + 1, 13, top, bot),
    stroke: border,
    borderRadius: 2,
  });
}

// 15×15 combo-box dropdown button.
function drawComboButton(
  ctx: AnyCtx,
  x: number,
  y: number,
  state: 'normal' | 'hover' | 'down' | 'disabled',
): void {
  drawSliderThumb(ctx, x, y, state);
  const arrowColor = state === 'disabled' ? PALETTE.textDisabled : PALETTE.textNormal;
  drawTriangle(ctx, x, y, 'bottom', arrowColor);
}

// 15×15 scroller arrow button. `dir` index matches GWEN's row index
// (0=Left, 1=Top, 2=Right, 3=Bottom).
//
// Painted at FULL 15×15 (no 1px inset) so the button sits flush with
// the scroll bar's edges. Earlier this reused drawSliderThumb which
// inset the thumb art to 13×13, leaving 1px transparent gutters all
// around — through those gutters the track underneath was visible
// only in narrow strips, and the perimeter of the bar lacked a crisp
// border. Now the button itself paints the bar's outer border at
// every edge it occupies, so top + bottom (or left + right for the
// horizontal bar) read as continuous outlined strokes.
function drawScrollerArrowButton(
  ctx: AnyCtx,
  x: number,
  y: number,
  state: 'normal' | 'hover' | 'down' | 'disabled',
  dirIdx: 0 | 1 | 2 | 3,
): void {
  let top: string;
  let bot: string;
  let border: string;
  if (state === 'normal') {
    top = PALETTE.buttonNormalTop;
    bot = PALETTE.buttonNormalBot;
    border = PALETTE.buttonBorder;
  } else if (state === 'hover') {
    top = PALETTE.buttonHoverTop;
    bot = PALETTE.buttonHoverBot;
    border = PALETTE.buttonBorder;
  } else if (state === 'down') {
    top = PALETTE.buttonPressedTop;
    bot = PALETTE.buttonPressedBot;
    border = PALETTE.buttonBorder;
  } else {
    top = PALETTE.buttonDisabled;
    bot = PALETTE.buttonDisabled;
    border = '#b0b0b0';
  }
  drawPatch(ctx, x, y, 15, 15, {
    fill: vGradient(ctx, x, y, 15, top, bot),
    stroke: border,
  });
  const arrowColor = state === 'disabled' ? PALETTE.textDisabled : PALETTE.textNormal;
  const dir: ArrowDir = dirIdx === 0 ? 'left' : dirIdx === 1 ? 'top' : dirIdx === 2 ? 'right' : 'bottom';
  drawTriangle(ctx, x, y, dir, arrowColor);
}

// ---------- Region painter (one switch, all 118 entries) ----------

function drawRegion(ctx: AnyCtx, d: RegionDescriptor): void {
  const { x, y, w, h, name } = d;

  // ---- Helpers used by multiple cases ----
  const buttonNormal = (): void => {
    drawPatch(ctx, x, y, w, h, {
      fill: vGradient(ctx, x, y, h, PALETTE.buttonNormalTop, PALETTE.buttonNormalBot),
      stroke: PALETTE.buttonBorder,
      borderRadius: 3,
      bevelLight: 'rgba(255,255,255,0.5)',
    });
  };
  const buttonHover = (): void => {
    drawPatch(ctx, x, y, w, h, {
      fill: vGradient(ctx, x, y, h, PALETTE.buttonHoverTop, PALETTE.buttonHoverBot),
      stroke: PALETTE.accent,
      borderRadius: 3,
      bevelLight: 'rgba(255,255,255,0.6)',
    });
  };
  const buttonDown = (): void => {
    drawPatch(ctx, x, y, w, h, {
      fill: vGradient(ctx, x, y, h, PALETTE.buttonPressedTop, PALETTE.buttonPressedBot),
      stroke: PALETTE.buttonBorder,
      borderRadius: 3,
      bevelDark: 'rgba(0,0,0,0.15)',
    });
  };
  const buttonDisabled = (): void => {
    drawPatch(ctx, x, y, w, h, {
      fill: PALETTE.buttonDisabled,
      stroke: '#b0b0b0',
      borderRadius: 3,
    });
  };

  switch (name) {
    // ---- Frame & feedback ----
    case 'Shadow': {
      // Soft drop shadow — paint as a smoothly-darkening radial.
      const grad = ctx.createRadialGradient(x + w / 2, y + h / 2, 4, x + w / 2, y + h / 2, w / 2);
      grad.addColorStop(0, 'rgba(0,0,0,0.55)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      drawPatch(ctx, x, y, w, h, { fill: grad, borderRadius: 8 });
      return;
    }
    case 'Tooltip': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.tooltipBg,
        stroke: PALETTE.tooltipBorder,
        borderRadius: 2,
      });
      return;
    }
    case 'StatusBar': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.statusBarBg,
        stroke: PALETTE.panelBorder,
        bevelLight: 'rgba(255,255,255,0.5)',
      });
      return;
    }
    case 'Selection': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.selection,
        borderRadius: 2,
      });
      return;
    }

    // ---- Panels ----
    case 'Panel.Normal': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelFill,
        stroke: PALETTE.panelBorder,
        borderRadius: 4,
      });
      return;
    }
    case 'Panel.Bright': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelBright,
        stroke: PALETTE.panelBorder,
        borderRadius: 4,
      });
      return;
    }
    case 'Panel.Dark': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelDark,
        stroke: PALETTE.panelBorder,
        borderRadius: 4,
      });
      return;
    }
    case 'Panel.Highlight': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelHighlight,
        stroke: PALETTE.accent,
        borderRadius: 4,
      });
      return;
    }

    // ---- Window frames ----
    case 'Window.Normal':
    case 'Window.Inactive': {
      const inactive = name === 'Window.Inactive';
      // Body.
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelFill,
        stroke: inactive ? '#909090' : '#205c90',
        borderRadius: 6,
      });
      // Title bar — height matches Window.Normal's mt so the painted
      // band lines up exactly with the WindowControl's Dragger. No
      // separator line at the bottom (previously baked a 1px stripe
      // that visibly spanned the window).
      const titleTop = inactive ? PALETTE.titleInactiveTop : PALETTE.titleActiveTop;
      const titleBot = inactive ? PALETTE.titleInactiveBot : PALETTE.titleActiveBottom;
      ctx.save();
      roundRectPath(ctx, x + 1, y + 1, w - 2, 26, 5);
      ctx.clip();
      ctx.fillStyle = vGradient(ctx, x, y, 28, titleTop, titleBot);
      ctx.fillRect(x, y, w, 28);
      ctx.restore();
      return;
    }

    // ---- Window controls ----
    case 'Window.Close': drawWindowControl(ctx, x, y, 'normal', 'close'); return;
    case 'Window.Close_Hover': drawWindowControl(ctx, x, y, 'hover', 'close'); return;
    case 'Window.Close_Down': drawWindowControl(ctx, x, y, 'down', 'close'); return;
    case 'Window.Mini': drawWindowControl(ctx, x, y, 'normal', 'mini'); return;
    case 'Window.Mini_Hover': drawWindowControl(ctx, x, y, 'hover', 'mini'); return;
    case 'Window.Mini_Down': drawWindowControl(ctx, x, y, 'down', 'mini'); return;
    case 'Window.Maxi': drawWindowControl(ctx, x, y, 'normal', 'maxi'); return;
    case 'Window.Maxi_Hover': drawWindowControl(ctx, x, y, 'hover', 'maxi'); return;
    case 'Window.Maxi_Down': drawWindowControl(ctx, x, y, 'down', 'maxi'); return;
    case 'Window.Restore': drawWindowControl(ctx, x, y, 'normal', 'restore'); return;
    case 'Window.Restore_Hover': drawWindowControl(ctx, x, y, 'hover', 'restore'); return;
    case 'Window.Restore_Down': drawWindowControl(ctx, x, y, 'down', 'restore'); return;

    // ---- Checkbox ----
    case 'Checkbox.Active.Normal': {
      drawPatch(ctx, x, y, 15, 15, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.buttonBorder,
      });
      return;
    }
    case 'Checkbox.Active.Checked': {
      drawPatch(ctx, x, y, 15, 15, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.buttonBorder,
      });
      drawCheckmark(ctx, x, y, PALETTE.textNormal);
      return;
    }
    case 'Checkbox.Disabled.Normal': {
      drawPatch(ctx, x, y, 15, 15, {
        fill: PALETTE.buttonDisabled,
        stroke: '#b0b0b0',
      });
      return;
    }
    case 'Checkbox.Disabled.Checked': {
      drawPatch(ctx, x, y, 15, 15, {
        fill: PALETTE.buttonDisabled,
        stroke: '#b0b0b0',
      });
      drawCheckmark(ctx, x, y, PALETTE.textDisabled);
      return;
    }

    // ---- Radio button ----
    case 'RadioButton.Active.Normal': {
      drawCircleFrame(ctx, x, y, PALETTE.textboxBg, PALETTE.buttonBorder);
      return;
    }
    case 'RadioButton.Active.Checked': {
      drawCircleFrame(ctx, x, y, PALETTE.textboxBg, PALETTE.buttonBorder);
      drawRadioDot(ctx, x, y, PALETTE.textNormal);
      return;
    }
    case 'RadioButton.Disabled.Normal': {
      drawCircleFrame(ctx, x, y, PALETTE.buttonDisabled, '#b0b0b0');
      return;
    }
    case 'RadioButton.Disabled.Checked': {
      drawCircleFrame(ctx, x, y, PALETTE.buttonDisabled, '#b0b0b0');
      drawRadioDot(ctx, x, y, PALETTE.textDisabled);
      return;
    }

    // ---- Text box ----
    case 'TextBox.Normal': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.textboxBorder,
      });
      return;
    }
    case 'TextBox.Focus': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.textboxFocused,
      });
      // Inner highlight to indicate focus.
      ctx.fillStyle = 'rgba(72,144,196,0.12)';
      ctx.fillRect(x + 1, y + 1, w - 2, h - 2);
      return;
    }
    case 'TextBox.Disabled': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.buttonDisabled,
        stroke: '#b0b0b0',
      });
      return;
    }

    // ---- Menu ----
    case 'Menu.Strip': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.menuStripBg,
        bevelLight: '#ffffff',
        bevelDark: '#a0a0a0',
      });
      return;
    }
    case 'Menu.BackgroundWithMargin':
    case 'Menu.Background': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelFill,
        stroke: PALETTE.panelBorder,
      });
      if (name === 'Menu.BackgroundWithMargin') {
        // 24px left gutter for icons / accelerators.
        ctx.fillStyle = '#dcdcdc';
        ctx.fillRect(x + 1, y + 1, 22, h - 2);
        ctx.fillStyle = PALETTE.panelBorder;
        ctx.fillRect(x + 23, y + 1, 1, h - 2);
      }
      return;
    }
    case 'Menu.Hover': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.menuHoverBg,
        borderRadius: 2,
      });
      return;
    }
    case 'Menu.RightArrow': {
      drawTriangle(ctx, x, y, 'right', PALETTE.textNormal);
      return;
    }
    case 'Menu.Check': {
      drawCheckmark(ctx, x, y, PALETTE.textNormal);
      return;
    }

    // ---- Tab ----
    case 'Tab.Control': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelFill,
        stroke: PALETTE.panelBorder,
        borderRadius: 3,
      });
      return;
    }
    case 'Tab.HeaderBar': {
      drawPatch(ctx, x, y, w, h, {
        fill: vGradient(ctx, x, y, h, '#e8e8e8', '#cfcfcf'),
        stroke: PALETTE.panelBorder,
      });
      return;
    }
    case 'Tab.Top.Active':
    case 'Tab.Bottom.Active':
    case 'Tab.Left.Active':
    case 'Tab.Right.Active': {
      drawPatch(ctx, x, y, w, h, {
        fill: vGradient(ctx, x, y, h, PALETTE.tabActiveTop, PALETTE.tabActiveBot),
        stroke: PALETTE.panelBorder,
        borderRadius: 3,
      });
      return;
    }
    case 'Tab.Top.Inactive':
    case 'Tab.Bottom.Inactive':
    case 'Tab.Left.Inactive':
    case 'Tab.Right.Inactive': {
      drawPatch(ctx, x, y, w, h, {
        fill: vGradient(ctx, x, y, h, PALETTE.tabInactiveTop, PALETTE.tabInactiveBot),
        stroke: PALETTE.panelBorder,
        borderRadius: 3,
      });
      return;
    }

    // ---- Tree ----
    case 'Tree.Background': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.panelBorder,
      });
      return;
    }
    case 'Tree.Plus': {
      // Centre the 9×9 glyph inside the 15×15 atlas cell. Without
      // the (15-9)/2 = 3px offset the art sits in the top-left of
      // the cell, and TreeNode's 15×15 toggle button — which blits
      // the whole region 1:1 — renders the [+] at the top-left of
      // its bounds (visibly above the title text and shifted left).
      drawPatch(ctx, x + 3, y + 3, 9, 9, {
        fill: PALETTE.textboxBg,
        stroke: '#808080',
      });
      ctx.fillStyle = PALETTE.textNormal;
      ctx.fillRect(x + 5, y + 7, 5, 1);
      ctx.fillRect(x + 7, y + 5, 1, 5);
      return;
    }
    case 'Tree.Minus': {
      // Same centering offset as Tree.Plus (see above).
      drawPatch(ctx, x + 3, y + 3, 9, 9, {
        fill: PALETTE.textboxBg,
        stroke: '#808080',
      });
      ctx.fillStyle = PALETTE.textNormal;
      ctx.fillRect(x + 5, y + 7, 5, 1);
      return;
    }

    // ---- Input.Button ----
    case 'Input.Button.Normal': buttonNormal(); return;
    case 'Input.Button.Hovered': buttonHover(); return;
    case 'Input.Button.Pressed': buttonDown(); return;
    case 'Input.Button.Disabled': buttonDisabled(); return;

    // ---- Scroller arrow buttons ----
    case 'Scroller.Button.Normal[0]': drawScrollerArrowButton(ctx, x, y, 'normal', 0); return;
    case 'Scroller.Button.Normal[1]': drawScrollerArrowButton(ctx, x, y, 'normal', 1); return;
    case 'Scroller.Button.Normal[2]': drawScrollerArrowButton(ctx, x, y, 'normal', 2); return;
    case 'Scroller.Button.Normal[3]': drawScrollerArrowButton(ctx, x, y, 'normal', 3); return;
    case 'Scroller.Button.Hover[0]': drawScrollerArrowButton(ctx, x, y, 'hover', 0); return;
    case 'Scroller.Button.Hover[1]': drawScrollerArrowButton(ctx, x, y, 'hover', 1); return;
    case 'Scroller.Button.Hover[2]': drawScrollerArrowButton(ctx, x, y, 'hover', 2); return;
    case 'Scroller.Button.Hover[3]': drawScrollerArrowButton(ctx, x, y, 'hover', 3); return;
    case 'Scroller.Button.Down[0]': drawScrollerArrowButton(ctx, x, y, 'down', 0); return;
    case 'Scroller.Button.Down[1]': drawScrollerArrowButton(ctx, x, y, 'down', 1); return;
    case 'Scroller.Button.Down[2]': drawScrollerArrowButton(ctx, x, y, 'down', 2); return;
    case 'Scroller.Button.Down[3]': drawScrollerArrowButton(ctx, x, y, 'down', 3); return;
    // Disabled arrow buckets live at x>=512 (off-canvas in the upstream
    // layout). Skip painting; consumers fall back to Scroller.Button.Normal.
    case 'Scroller.Button.Disabled[0]':
    case 'Scroller.Button.Disabled[1]':
    case 'Scroller.Button.Disabled[2]':
    case 'Scroller.Button.Disabled[3]':
      return;

    // ---- Scroller bars/tracks ----
    case 'Scroller.TrackV':
    case 'Scroller.TrackH': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.scrollTrack,
        stroke: PALETTE.scrollTrackBorder,
      });
      return;
    }
    case 'Scroller.ButtonV_Normal':
    case 'Scroller.ButtonH_Normal': {
      const horiz = name === 'Scroller.ButtonH_Normal';
      drawPatch(ctx, x, y, w, h, {
        fill: horiz
          ? vGradient(ctx, x, y, h, PALETTE.scrollThumbTop, PALETTE.scrollThumbBot)
          : hGradient(ctx, x, y, w, PALETTE.scrollThumbTop, PALETTE.scrollThumbBot),
        stroke: PALETTE.scrollThumbBorder,
        borderRadius: 2,
      });
      return;
    }
    case 'Scroller.ButtonV_Hover':
    case 'Scroller.ButtonH_Hover': {
      const horiz = name === 'Scroller.ButtonH_Hover';
      drawPatch(ctx, x, y, w, h, {
        fill: horiz
          ? vGradient(ctx, x, y, h, PALETTE.buttonHoverTop, PALETTE.buttonHoverBot)
          : hGradient(ctx, x, y, w, PALETTE.buttonHoverTop, PALETTE.buttonHoverBot),
        stroke: PALETTE.accent,
        borderRadius: 2,
      });
      return;
    }
    case 'Scroller.ButtonV_Down':
    case 'Scroller.ButtonH_Down': {
      const horiz = name === 'Scroller.ButtonH_Down';
      drawPatch(ctx, x, y, w, h, {
        fill: horiz
          ? vGradient(ctx, x, y, h, PALETTE.buttonPressedTop, PALETTE.buttonPressedBot)
          : hGradient(ctx, x, y, w, PALETTE.buttonPressedTop, PALETTE.buttonPressedBot),
        stroke: PALETTE.buttonBorder,
        borderRadius: 2,
      });
      return;
    }
    case 'Scroller.ButtonV_Disabled':
    case 'Scroller.ButtonH_Disabled': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.buttonDisabled,
        stroke: '#b0b0b0',
        borderRadius: 2,
      });
      return;
    }

    // ---- Input.ListBox ----
    case 'Input.ListBox.Background': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.panelBorder,
      });
      return;
    }
    case 'Input.ListBox.Hovered': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.buttonHoverBot,
      });
      return;
    }
    case 'Input.ListBox.EvenLine': {
      // Transparent — even rows show through to the background.
      return;
    }
    case 'Input.ListBox.OddLine': {
      drawPatch(ctx, x, y, w, h, {
        fill: '#f4f4f4',
      });
      return;
    }
    case 'Input.ListBox.EvenLineSelected':
    case 'Input.ListBox.OddLineSelected': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.selection,
      });
      return;
    }

    // ---- Input.ComboBox ----
    case 'Input.ComboBox.Normal': buttonNormal(); return;
    case 'Input.ComboBox.Hover': buttonHover(); return;
    case 'Input.ComboBox.Down': buttonDown(); return;
    case 'Input.ComboBox.Disabled': buttonDisabled(); return;
    case 'Input.ComboBox.Button.Normal': drawComboButton(ctx, x, y, 'normal'); return;
    case 'Input.ComboBox.Button.Hover': drawComboButton(ctx, x, y, 'hover'); return;
    case 'Input.ComboBox.Button.Down': drawComboButton(ctx, x, y, 'down'); return;
    case 'Input.ComboBox.Button.Disabled': drawComboButton(ctx, x, y, 'disabled'); return;

    // ---- Input.UpDown ----
    case 'Input.UpDown.Up.Normal': drawSpinnerArrow(ctx, x, y, true, PALETTE.textNormal); return;
    case 'Input.UpDown.Up.Hover': drawSpinnerArrow(ctx, x, y, true, PALETTE.accent); return;
    case 'Input.UpDown.Up.Down': drawSpinnerArrow(ctx, x, y, true, '#205c90'); return;
    case 'Input.UpDown.Up.Disabled': drawSpinnerArrow(ctx, x, y, true, PALETTE.textDisabled); return;
    case 'Input.UpDown.Down.Normal': drawSpinnerArrow(ctx, x, y, false, PALETTE.textNormal); return;
    case 'Input.UpDown.Down.Hover': drawSpinnerArrow(ctx, x, y, false, PALETTE.accent); return;
    case 'Input.UpDown.Down.Down': drawSpinnerArrow(ctx, x, y, false, '#205c90'); return;
    case 'Input.UpDown.Down.Disabled': drawSpinnerArrow(ctx, x, y, false, PALETTE.textDisabled); return;

    // ---- ProgressBar ----
    case 'ProgressBar.Back': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.progressBack,
        stroke: PALETTE.panelBorder,
        borderRadius: 2,
      });
      return;
    }
    case 'ProgressBar.Front': {
      drawPatch(ctx, x, y, w, h, {
        fill: vGradient(ctx, x, y, h, '#33e858', PALETTE.progressFront),
        stroke: '#008818',
        borderRadius: 2,
      });
      return;
    }

    // ---- Input.Slider thumbs ----
    case 'Input.Slider.H.Normal': drawSliderThumb(ctx, x, y, 'normal'); return;
    case 'Input.Slider.H.Hover': drawSliderThumb(ctx, x, y, 'hover'); return;
    case 'Input.Slider.H.Down': drawSliderThumb(ctx, x, y, 'down'); return;
    case 'Input.Slider.H.Disabled': drawSliderThumb(ctx, x, y, 'disabled'); return;
    case 'Input.Slider.V.Normal': drawSliderThumb(ctx, x, y, 'normal'); return;
    case 'Input.Slider.V.Hover': drawSliderThumb(ctx, x, y, 'hover'); return;
    case 'Input.Slider.V.Down': drawSliderThumb(ctx, x, y, 'down'); return;
    case 'Input.Slider.V.Disabled': drawSliderThumb(ctx, x, y, 'disabled'); return;

    // ---- CategoryList ----
    case 'CategoryList.Outer': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.panelFill,
        stroke: PALETTE.panelBorder,
        borderRadius: 4,
      });
      return;
    }
    case 'CategoryList.Inner': {
      drawPatch(ctx, x, y, w, h, {
        fill: PALETTE.textboxBg,
        stroke: PALETTE.panelBorder,
      });
      // 21px header gutter (matches m=[8,21,8,8]). The gradient + dark
      // separator must live ENTIRELY inside the top 9-slice patch
      // (rows 0..mt-1 = 0..20). Painting the separator at row 21 — the
      // first row of the center patch — makes vertical stretching smear
      // the dark line across the body. We paint the separator at row 19
      // and leave row 20 as the textbox-white fill so the boundary
      // between top and center patches blends white-to-white.
      ctx.fillStyle = vGradient(ctx, x + 1, y + 1, 18, PALETTE.titleActiveTop, PALETTE.titleActiveBottom);
      ctx.fillRect(x + 1, y + 1, w - 2, 18);
      ctx.fillStyle = '#205c90';
      ctx.fillRect(x + 1, y + 19, w - 2, 1);
      return;
    }
    case 'CategoryList.Header': {
      drawPatch(ctx, x, y, w, h, {
        fill: vGradient(ctx, x, y, h, PALETTE.titleActiveTop, PALETTE.titleActiveBottom),
        stroke: '#205c90',
        borderRadius: 3,
      });
      return;
    }

    // ---- GroupBox ----
    case 'GroupBox': {
      drawPatch(ctx, x, y, w, h, {
        fill: 'rgba(0,0,0,0)',
        stroke: PALETTE.panelBorder,
        borderRadius: 3,
      });
      return;
    }

    default: {
      // Unhandled descriptor — paint magenta so missing skin work shows up
      // on screen instead of looking blank.
      drawPatch(ctx, x, y, w, h, { fill: '#ff00ff' });
      return;
    }
  }
}

// ---------- Class ----------

export class DynamicSkin {
  readonly regions: ReadonlyMap<string, RegionInfo>;
  readonly colors: SkinColors;

  private readonly renderer: WebGL2Renderer;
  private readonly tex: Texture = texture('DynamicSkin');
  private readonly regionMap = new Map<string, RegionInfo>();
  private initialized = false;

  constructor(renderer: WebGL2Renderer) {
    this.renderer = renderer;
    this.regions = this.regionMap;
    this.colors = buildSkinColors();
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    const canvas = makeAtlasCanvas();
    const ctx = canvas.getContext('2d') as AnyCtx | null;
    if (!ctx) {
      throw new Error('DynamicSkin: 2D context unavailable');
    }
    ctx.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
    ctx.imageSmoothingEnabled = false;

    // Paint each region.
    for (const d of REGIONS) {
      // Skip out-of-bounds cells (Scroller.Button.Disabled[*] live at x=528).
      if (d.x >= ATLAS_SIZE || d.y >= ATLAS_SIZE) {
        this.recordRegion(d);
        continue;
      }
      drawRegion(ctx, d);
      this.recordRegion(d);
    }

    // Bake the palette strip GWEN reads via PixelColour (TexturedBase.h:297).
    bakeColorStrip(ctx);

    // Upload to GPU.
    this.renderer.loadTextureFromSource(this.tex, canvas as TexImageSource);
    this.tex.width = ATLAS_SIZE;
    this.tex.height = ATLAS_SIZE;
    this.tex.name = 'DynamicSkin';
  }

  getTexture(): Texture {
    return this.tex;
  }

  private recordRegion(d: RegionDescriptor): void {
    const inv = 1 / ATLAS_SIZE;
    const uv: [number, number, number, number] = [
      d.x * inv,
      d.y * inv,
      (d.x + d.w) * inv,
      (d.y + d.h) * inv,
    ];
    if (d.type === 'bordered') {
      this.regionMap.set(d.name, {
        type: 'bordered',
        x: d.x,
        y: d.y,
        w: d.w,
        h: d.h,
        marginLeft: d.m[0],
        marginTop: d.m[1],
        marginRight: d.m[2],
        marginBottom: d.m[3],
        uv,
      });
    } else {
      this.regionMap.set(d.name, {
        type: 'single',
        x: d.x,
        y: d.y,
        w: d.w,
        h: d.h,
        uv,
      });
    }
  }
}

function bakeColorStrip(ctx: AnyCtx): void {
  for (let n = 0; n < BAKED_ROW_508.length; n++) {
    ctx.fillStyle = BAKED_ROW_508[n].hex;
    ctx.fillRect(4 + 8 * n, 508, 8, 4);
  }
  for (let n = 0; n < BAKED_ROW_500.length; n++) {
    ctx.fillStyle = BAKED_ROW_500[n].hex;
    ctx.fillRect(4 + 8 * n, 500, 8, 4);
  }
}

// Builds the SkinColors record from the same PALETTE / BAKED_ROW_* tables
// the painter uses. We avoid an async readPixels round-trip by sourcing the
// colors directly — they're guaranteed to match what got baked into the
// strip because they're the same string constants.
function buildSkinColors(): SkinColors {
  const find = (row: readonly { name: string; hex: string }[], name: string): Color => {
    for (let i = 0; i < row.length; i++) {
      if (row[i].name === name) return parseHex(row[i].hex);
    }
    return color(255, 0, 255, 255); // magenta sentinel — should never happen
  };
  return {
    window: {
      titleActive: find(BAKED_ROW_508, 'Window.TitleActive'),
      titleInactive: find(BAKED_ROW_508, 'Window.TitleInactive'),
    },
    button: {
      normal: find(BAKED_ROW_508, 'Button.Normal'),
      hover: find(BAKED_ROW_508, 'Button.Hover'),
      down: find(BAKED_ROW_500, 'Button.Down'),
      disabled: find(BAKED_ROW_500, 'Button.Disabled'),
      bright: parseHex(PALETTE.panelBright),
      dark: parseHex(PALETTE.panelDark),
    },
    tab: {
      active: { normal: find(BAKED_ROW_508, 'Tab.Active.Normal') },
      inactive: { normal: find(BAKED_ROW_508, 'Tab.Inactive.Normal') },
    },
    label: {
      default: find(BAKED_ROW_508, 'Label.Default'),
      bright: find(BAKED_ROW_508, 'Label.Bright'),
      dark: find(BAKED_ROW_500, 'Label.Dark'),
      highlight: find(BAKED_ROW_500, 'Label.Highlight'),
    },
    tree: {
      lines: find(BAKED_ROW_508, 'Tree.Lines'),
      normal: find(BAKED_ROW_508, 'Tree.Normal'),
      hover: find(BAKED_ROW_500, 'Tree.Hover'),
      selected: find(BAKED_ROW_500, 'Tree.Selected'),
    },
    properties: {
      line_normal: find(BAKED_ROW_508, 'Properties.Line_Normal'),
      line_selected: find(BAKED_ROW_508, 'Properties.Line_Selected'),
      line_hover: find(BAKED_ROW_500, 'Properties.Line_Hover'),
      title: find(BAKED_ROW_500, 'Properties.Title'),
      column_normal: find(BAKED_ROW_508, 'Properties.Column_Normal'),
      column_selected: find(BAKED_ROW_508, 'Properties.Column_Selected'),
      column_hover: find(BAKED_ROW_500, 'Properties.Column_Hover'),
      label_normal: find(BAKED_ROW_508, 'Properties.Label_Normal'),
      label_selected: find(BAKED_ROW_508, 'Properties.Label_Selected'),
      label_hover: find(BAKED_ROW_500, 'Properties.Label_Hover'),
      border: find(BAKED_ROW_500, 'Properties.Border'),
    },
    modalBackground: find(BAKED_ROW_508, 'ModalBackground'),
    tooltipText: find(BAKED_ROW_508, 'TooltipText'),
    category: {
      header: find(BAKED_ROW_500, 'Category.Header'),
      header_closed: find(BAKED_ROW_500, 'Category.Header_Closed'),
      line: {
        text: find(BAKED_ROW_508, 'Category.Line.Text'),
        text_hover: find(BAKED_ROW_508, 'Category.Line.Text_Hover'),
        text_selected: find(BAKED_ROW_500, 'Category.Line.Text_Selected'),
        button: find(BAKED_ROW_500, 'Category.Line.Button'),
        button_hover: find(BAKED_ROW_508, 'Category.Line.Button_Hover'),
        button_selected: find(BAKED_ROW_508, 'Category.Line.Button_Selected'),
      },
      line_alt: {
        text: find(BAKED_ROW_500, 'Category.LineAlt.Text'),
        text_hover: find(BAKED_ROW_500, 'Category.LineAlt.Text_Hover'),
        text_selected: find(BAKED_ROW_508, 'Category.LineAlt.Text_Selected'),
        button: find(BAKED_ROW_508, 'Category.LineAlt.Button'),
        button_hover: find(BAKED_ROW_500, 'Category.LineAlt.Button_Hover'),
        button_selected: find(BAKED_ROW_500, 'Category.LineAlt.Button_Selected'),
      },
    },
  };
}
